import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslate } from 'react-admin'
import { useFormikContext } from 'formik'
import { useQuery } from 'react-query'
import { Collapse, Divider, Grid, List, ListItem, ListItemText, makeStyles } from '@material-ui/core'
import QueryKeys from '../../../utils/constants/queryKeys'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../../utils/constants/gameEventFormBroadcastFields'
import getRecentLivestreams from '../../../Apis/livestream/getRecentLivestreams'
import { RECENT_BROADCASTS_LIMIT } from '../../../utils/constants/broadcastStates'
import ConditionalRender from '../../ConditionalRender/ConditionalRender'
import selectRecentLivestreamDialogStyles from './selectRecentLivestreamDialog.styles'
import localStorageHelper from '../../../utils/helpers/localStorageHelper'
import LoaderDots from '../../loaders/LoaderDots/LoaderDots'
import orgSettingPageStyles from '../../../pages/OrgSettings/OrgSettingsPage/orgSettingsPage.styles'
import localStorageIds from '../../../utils/constants/localStorageIds'
import { canUseOrgYoutube } from '../../../utils/helpers/youtubeLink'

const YOUTUBE_RTMP_BASE = 'rtmp://a.rtmp.youtube.com/live2/'
// Recent broadcasts store a full rtmp URL; the stream-key field wants the bare key for YouTube
// and the full URL for a custom server.
const deriveStreamKey = (url) => {
  if (!url) return null
  return url.startsWith(YOUTUBE_RTMP_BASE) ? url.slice(YOUTUBE_RTMP_BASE.length) : url
}

const SelectRecentLivestreamDialog = (props) => {
  const { expanded, onCollapse } = props
  const [recentBroadcasts, setRecentBroadcasts] = useState([])
  const translate = useTranslate()
  const { values, setValues } = useFormikContext()
  const { localStorageValue } = localStorageHelper(localStorageIds.SELECTED_ORG)
  // A dead link means a reused YouTube setup would only fail on save, so those recent
  // broadcasts are filtered out exactly as they are for an org that never linked one.
  const hasSetYoutube = canUseOrgYoutube(localStorageValue)
  const useStyles = makeStyles(selectRecentLivestreamDialogStyles)
  const classes = useStyles()

  const { isLoading } = useQuery(
    [QueryKeys.GET_RECENT_BROADCASTS, localStorageValue?.id],
    () => getRecentLivestreams({ orgId: localStorageValue?.id, limit: RECENT_BROADCASTS_LIMIT }),
    {
      onSuccess: (res) => {
        // Don't show the ones with yt configuration if signed out
        // TODO: This check should be happening on the be, because now it may not show any recent broadcasts if all of the recent are yt
        setRecentBroadcasts(
          hasSetYoutube
            ? res
            : res.filter(
                (rec) =>
                  !Object.values(rec?.eventYtConfig || {}).filter((val) => val !== null)?.length,
              ),
        )
      },
    },
  )

  const applyBroadcast = (selectedBroadcast) => {
    if (!selectedBroadcast) return

    // The API serializes with Newtonsoft (camelCase); the DTO's [JsonPropertyName] snake_case
    // attributes are ignored, so the payload is camelCase.
    const ytConfig = selectedBroadcast.eventYtConfig
    const hasYoutubeConfig = !!ytConfig?.broadcastTitle

    const next = {
      ...values,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_LEFT_LOGO]: selectedBroadcast.courtTlLogoUrl || null,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_TOP_RIGHT_LOGO]: selectedBroadcast.courtTrLogoUrl || null,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_LEFT_LOGO]: selectedBroadcast.courtBlLogoUrl || null,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_BOTTOM_RIGHT_LOGO]:
        selectedBroadcast.courtBrLogoUrl || null,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_CENTER_LOGO]: selectedBroadcast.courtCntrLogoUrl || null,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.AUTO_START]: selectedBroadcast.autoStart,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.AUTO_STOP]: selectedBroadcast.autoStop,
      [GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO]:
        selectedBroadcast.gameStateTrackingType === 'auto',
      [GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_MANUAL]:
        selectedBroadcast.gameStateTrackingType === 'manual',
      [GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE]: selectedBroadcast.type
        ? selectedBroadcast.type.toUpperCase()
        : values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE],
      studioOperatorId: selectedBroadcast.studioOperatorId ?? null,
    }

    if (hasYoutubeConfig) {
      // Managed YouTube: carry visibility only (not title/thumbnail); clear any manual key so the
      // two destinations stay mutually exclusive.
      next[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG] = {
        ...(values[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG] || {}),
        [GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_VISIBILITY]: ytConfig.visibility ?? null,
      }
      next[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION] = null
    } else {
      // Manual stream key / custom RTMP: carry the destination over and clear any YouTube config.
      next[GAME_EVENT_FORM_BROADCAST_FIELDS.STREAM_DESTINATION] = deriveStreamKey(
        selectedBroadcast.streamerOutputUrl,
      )
      next[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG] = null
    }

    setValues(next)
    onCollapse()
  }

  return (
    <Collapse in={expanded} timeout={250} unmountOnExit>
      <div className={classes.inlinePanel}>
        <List component='nav' disablePadding>
          <ConditionalRender renderValue={isLoading}>
            <Grid container justifyContent='center'>
              <LoaderDots className={orgSettingPageStyles.loader} />
            </Grid>
          </ConditionalRender>

          {recentBroadcasts?.length > 0 ? (
            recentBroadcasts.map((livestream, index) => (
              <div key={livestream.uuid}>
                <ListItem button onClick={() => applyBroadcast(livestream)}>
                  <ListItemText
                    primary={livestream?.name}
                    primaryTypographyProps={{ className: classes.listItemText }}
                  />
                </ListItem>

                <ConditionalRender renderValue={index !== recentBroadcasts?.length - 1}>
                  <Divider />
                </ConditionalRender>
              </div>
            ))
          ) : (
            <ConditionalRender renderValue={!isLoading}>
              <span className={classes.emptyText}>{translate('ra.text.noRecentLivestream')}</span>
            </ConditionalRender>
          )}
        </List>
      </div>
    </Collapse>
  )
}

SelectRecentLivestreamDialog.propTypes = {
  expanded: PropTypes.bool,
  onCollapse: PropTypes.func,
}

export default SelectRecentLivestreamDialog
